/**
 * Onboarding (TZ §10.2).
 *
 * Har bir ekran — BITTA savol, BITTA bosish. t("onboarding.skip") doim
 * mavjud: hech narsa majburiy emas (T1).
 *
 * Telefon raqami bu yerda SO'RALMAYDI (TZ §10.3): qiymat ko'rsatilmasdan
 * so'ralgan telefon 30-50% foydalanuvchini yo'qotadi.
 */

import { t, tp } from "../i18n";
import { useState } from "react";
import { api } from "../api";
import {
  FIELD_KEYS, IELTS_KEYS, STATUS_KEYS,
  fieldLabel, ieltsLabel, statusLabel,
} from "../format";
import { IconCheck } from "../icons";
import { navigate } from "../router";
import { haptic } from "../telegram";
import { Spinner } from "../components";

const STATUSES = STATUS_KEYS;
const FIELDS = FIELD_KEYS.filter((f) => f !== "any");
const IELTS = IELTS_KEYS;

const CURRENT_YEAR = new Date().getFullYear();
// Auditoriya: maktab o'quvchisidan 35 yoshli mutaxassisgacha (TZ §3)
const YEARS = Array.from({ length: 40 }, (_, i) => CURRENT_YEAR - 14 - i);

interface Answers {
  status?: string;
  birth_year?: number;
  fields: string[];
  ielts_status?: string;
}

export default function Onboarding({ onDone }: { onDone: () => void }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({ fields: [] });
  const [matchCount, setMatchCount] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function finish(final: Answers) {
    setSaving(true);
    setError(null);
    try {
      const result = await api.onboarding({
        status: final.status ?? null,
        birth_year: final.birth_year ?? null,
        fields: final.fields,
        ielts_status: final.ielts_status ?? null,
      });
      setMatchCount(result.match_count);
      setStep(4);
      haptic("success");
    } catch {
      setError(t("onboarding.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  function advance(patch: Partial<Answers>) {
    haptic("light");
    const next = { ...answers, ...patch };
    setAnswers(next);
    if (step === 3) void finish(next);
    else setStep(step + 1);
  }

  if (saving) return <Spinner label={t("onboarding.saving")} />;

  const progress = (
    <div className="progress">
      {[0, 1, 2, 3].map((i) => (
        <span key={i} className={`dot${i <= step ? " dot-on" : ""}`} />
      ))}
    </div>
  );

  const skip = (
    <button className="link-btn" type="button" onClick={() => advance({})}>
      {t("onboarding.skip")}
    </button>
  );

  if (step === 4) {
    return (
      <div className="onboarding center">
        <div className="state-icon state-icon-done">
          <IconCheck size={28} />
        </div>
        <h1>{t("onboarding.done")}</h1>
        {/* Aniq raqam qiymatni DARHOL isbotlaydi (TZ §10.2, Ekran 5).

            Jumla lug'atda BUTUN turadi, faqat raqam qalin chiziladi.
            Uni ikki kalitga bo'lish oson bo'lardi, lekin o'shanda
            tarjimon jumlaning qanday tugashini ko'rmasdi va boshqa
            tilda so'z tartibi buzilardi: rus tilida raqam BOSHIDA
            emas, o'rtasida turadi («Нашли 5 подходящих возможностей»).

            Shuning uchun matn to'liq olinadi va raqam o'rniga
            ajratgich qo'yiladi — u yerdan ikkiga bo'linadi. */}
        <p className="match-count">
          {(() => {
            const n = matchCount ?? 0;
            // O'rinbosarning O'ZI qaytariladi, keyin shu bo'yicha
            // bo'linadi. Maxsus ajratgich belgisi kerak emas:
            // `{n}` matnda hech qachon uchramaydi va u ko'rinib turadi.
            const [before, after] = tp("onboarding.found", n, { n: "{n}" })
              .split("{n}");
            return (
              <>
                {before}
                <b>{n}</b>
                {after ?? ""}
              </>
            );
          })()}
        </p>
        <button
          className="btn btn-primary"
          type="button"
          onClick={() => {
            navigate({ name: "home" }, { replace: true });
            onDone();
          }}
        >
          {t("onboarding.view")}
        </button>
      </div>
    );
  }

  return (
    <div className="onboarding">
      {progress}
      {error ? <p className="error">{error}</p> : null}

      {step === 0 && (
        <>
          <h1>{t("onboarding.stageQuestion")}</h1>
          <p className="muted">
            {t("onboarding.stageHint")}
          </p>
          <div className="option-list">
            {STATUSES.map((key) => (
              <button
                key={key}
                className="option"
                type="button"
                onClick={() => advance({ status: key })}
              >
                {statusLabel(key)}
              </button>
            ))}
          </div>
          {skip}
        </>
      )}

      {step === 1 && (
        <>
          <h1>{t("onboarding.ageQuestion")}</h1>
          <p className="muted">
            {t("onboarding.ageHint")}
          </p>
          <select
            className="select"
            defaultValue=""
            onChange={(e) => {
              const value = Number(e.target.value);
              if (value) advance({ birth_year: value });
            }}
          >
            <option value="" disabled>
              {t("onboarding.birthYear")}
            </option>
            {YEARS.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          {skip}
        </>
      )}

      {step === 2 && (
        <>
          <h1>{t("onboarding.fieldsQuestion")}</h1>
          <p className="muted">{t("onboarding.multiHint")}</p>
          <div className="chip-grid">
            {FIELDS.map((key) => {
              const on = answers.fields.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  className={`chip${on ? " chip-active" : ""}`}
                  onClick={() => {
                    haptic("light");
                    setAnswers({
                      ...answers,
                      fields: on
                        ? answers.fields.filter((f) => f !== key)
                        : [...answers.fields, key],
                    });
                  }}
                >
                  {fieldLabel(key)}
                </button>
              );
            })}
          </div>
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => advance({})}
          >
            {t("onboarding.next")}
          </button>
          {skip}
        </>
      )}

      {step === 3 && (
        <>
          <h1>{t("onboarding.certQuestion")}</h1>
          <p className="muted">
            {t("onboarding.certHint")}
          </p>
          <div className="option-list">
            {IELTS.map((key) => (
              <button
                key={key}
                className="option"
                type="button"
                onClick={() => advance({ ielts_status: key })}
              >
                {ieltsLabel(key)}
              </button>
            ))}
          </div>
          {skip}
        </>
      )}
    </div>
  );
}
